import { ChevronDown } from 'lucide-react';
import { useId, useState, type KeyboardEvent } from 'react';
import type { CrawlJobMonitorDTO } from '../../types/lac';
import {
  buildLacCrawlJobDetailLines,
  resolveLacCrawlJobStartedBy,
} from '../../features/lac/crawlJobMonitorDetails';
import { useI18n } from '../../i18n/useI18n';
import type { I18nContextValue } from '../../types/i18nContext';
import { formatNumber, formatTimestamp } from '../../utils/formatUtils';
import { LacCrawlSelectedUrlsDialog } from './LacCrawlSelectedUrlsDialog';

type LacCrawlJobRowProps = {
  job: CrawlJobMonitorDTO & { crawlJobId: string };
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** When false, omit tenant from the meta line (tenant-scoped lists). */
  showTenant?: boolean;
};

function tenantLabel(job: CrawlJobMonitorDTO): string {
  return job.tenantDisplayName?.trim() || job.tenantName?.trim() || '—';
}

function formatCrawlJobStatus(
  status: CrawlJobMonitorDTO['status'],
  translate: I18nContextValue['translate'],
): string {
  if (status == null) {
    return translate('lac.websiteCrawls.statusUnknown');
  }
  return translate(`knowledge.crawlStatus.${status}`);
}

export function LacCrawlJobRow({
  job,
  isExpanded,
  onToggleExpanded,
  showTenant = true,
}: LacCrawlJobRowProps) {
  const { locale, translate } = useI18n();
  const panelId = useId();
  const titleId = useId();
  const [isUrlsDialogOpen, setIsUrlsDialogOpen] = useState(false);

  const detailLines = buildLacCrawlJobDetailLines(job, { translate, locale });
  const selectedNodes = job.selectedUrlNodes ?? [];
  const selectedUrlCount = selectedNodes.length;

  const url = job.startUrl?.trim() || '—';
  const startedLabel = job.startedAt
    ? formatTimestamp(job.startedAt, locale)
    : null;
  const finishedLabel = job.completedAt
    ? formatTimestamp(job.completedAt, locale)
    : null;
  const startedBy = resolveLacCrawlJobStartedBy(job);
  let timingMeta: string | null = null;
  if (startedLabel && finishedLabel) {
    timingMeta = `${startedLabel} → ${finishedLabel}`;
  } else if (startedLabel) {
    timingMeta = startedLabel;
  }
  if (timingMeta != null && startedBy != null) {
    timingMeta = `${timingMeta} (${startedBy})`;
  } else if (timingMeta == null && startedBy != null) {
    timingMeta = startedBy;
  }
  const knowledgeBaseName = job.knowledgeBaseName?.trim() || null;
  const tenantText = tenantLabel(job);
  const trailingMeta = [knowledgeBaseName, timingMeta].filter(
    (part): part is string => Boolean(part),
  );

  const urlCountLabel = translate('lac.websiteCrawls.urlCount', {
    count: formatNumber(selectedUrlCount, locale),
  });

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggleExpanded();
    }
  };

  return (
    <li className="lac-website-crawls__item">
      <div
        className="lac-website-crawls__header"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={onToggleExpanded}
        onKeyDown={handleHeaderKeyDown}
      >
        <div className="lac-website-crawls__lead">
          <ChevronDown
            className={[
              'lac-website-crawls__chevron',
              isExpanded && 'lac-website-crawls__chevron--expanded',
            ]
              .filter(Boolean)
              .join(' ')}
            strokeWidth={2}
            aria-hidden
          />

          <div className="lac-website-crawls__identity">
            <span id={titleId} className="lac-website-crawls__url" title={url}>
              {url}
            </span>
            <span className="lac-website-crawls__meta">
              {showTenant ? <span>{tenantText}</span> : null}
              {showTenant && trailingMeta.length > 0 ? ' · ' : null}
              {trailingMeta.join(' · ')}
            </span>
          </div>
        </div>

        {selectedUrlCount > 0 ? (
          <button
            type="button"
            className="lac-website-crawls__url-count"
            onClick={(event) => {
              event.stopPropagation();
              setIsUrlsDialogOpen(true);
            }}
            aria-label={translate('lac.websiteCrawls.urlCountOpen', {
              count: formatNumber(selectedUrlCount, locale),
            })}
          >
            {urlCountLabel}
          </button>
        ) : (
          <span className="lac-website-crawls__url-count lac-website-crawls__url-count--empty">
            {urlCountLabel}
          </span>
        )}

        <div className="lac-website-crawls__badges">
          {job.abandoned ? (
            <span className="knowledge-database__status knowledge-database__status--abandoned">
              {translate('lac.websiteCrawls.abandoned')}
            </span>
          ) : null}
          <span
            className={[
              'knowledge-database__status',
              job.status != null &&
                `knowledge-database__status--${job.status.toLowerCase()}`,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {formatCrawlJobStatus(job.status, translate)}
          </span>
        </div>
      </div>

      {isExpanded && detailLines.length > 0 ? (
        <div
          id={panelId}
          className="lac-website-crawls__details"
          aria-labelledby={titleId}
        >
          {detailLines.map((line, index) => (
            <p
              key={`${job.crawlJobId}-detail-${index}`}
              className={[
                'lac-website-crawls__detail-line',
                line.indent && 'lac-website-crawls__detail-line--indent',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {line.text}
            </p>
          ))}
        </div>
      ) : null}

      {isUrlsDialogOpen ? (
        <LacCrawlSelectedUrlsDialog
          startUrl={url}
          nodes={selectedNodes}
          onClose={() => setIsUrlsDialogOpen(false)}
        />
      ) : null}
    </li>
  );
}
